import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Hash, Lock, MessageCircle, Search, Forward } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForwardDialog = ({ open, onOpenChange, attachment, senderName }) => {
  const [channels, setChannels] = useState([]);
  const [dms, setDms] = useState([]);
  const [search, setSearch] = useState('');
  const [forwarding, setForwarding] = useState(false);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      axios.get(`${API}/teams`).then(async (teamsRes) => {
        const teams = teamsRes.data.teams || [];
        if (teams.length > 0) {
          const chRes = await axios.get(`${API}/channels?team_id=${teams[0].team_id}`);
          setChannels(chRes.data.channels || []);
        }
      }),
      axios.get(`${API}/dm/list`).then(res => setDms(res.data.dms || []))
    ]).catch(() => {});
  }, [open]);

  const handleForward = async (targetId, isDm) => {
    setForwarding(true);
    try {
      await axios.post(`${API}/messages/forward`, {
        target_channel_id: isDm ? null : targetId,
        target_dm_id: isDm ? targetId : null,
        attachment: attachment,
        original_sender_name: senderName || ''
      });
      toast.success('Forwarded successfully');
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to forward');
    } finally {
      setForwarding(false);
    }
  };

  const filteredChannels = channels.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()));
  const filteredDms = dms.filter(d => {
    const name = d.other_user?.display_name || d.other_user?.username || '';
    return name.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]" data-testid="forward-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Space Grotesk' }}>
            <Forward className="w-4 h-4" /> Forward Attachment
          </DialogTitle>
        </DialogHeader>

        {/* Preview */}
        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-[hsl(var(--surface-2))] text-sm">
          <span className="truncate flex-1 text-muted-foreground">{attachment?.original_name || attachment?.name || 'File'}</span>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search channels or users..."
            className="pl-8 h-9 text-sm"
            data-testid="forward-search-input"
          />
        </div>

        <ScrollArea className="max-h-[280px]">
          <div className="space-y-0.5">
            {/* Channels */}
            {filteredChannels.length > 0 && (
              <>
                <p className="text-xs font-medium text-muted-foreground px-2 py-1.5">Channels</p>
                {filteredChannels.map(c => (
                  <button
                    key={c.channel_id}
                    onClick={() => handleForward(c.channel_id, false)}
                    disabled={forwarding}
                    className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm hover:bg-[hsl(var(--message-hover))] transition-colors disabled:opacity-50"
                    data-testid={`forward-to-channel-${c.channel_id}`}
                  >
                    <div className="w-6 h-6 rounded-md bg-[hsl(var(--surface-2))] flex items-center justify-center">
                      {c.is_private ? <Lock className="w-3 h-3 text-muted-foreground" /> : <Hash className="w-3 h-3 text-muted-foreground" />}
                    </div>
                    <span className="flex-1 text-left truncate">{c.name}</span>
                  </button>
                ))}
              </>
            )}

            {/* DMs */}
            {filteredDms.length > 0 && (
              <>
                <p className="text-xs font-medium text-muted-foreground px-2 py-1.5 mt-2">Direct Messages</p>
                {filteredDms.map(d => (
                  <button
                    key={d.dm_id}
                    onClick={() => handleForward(d.dm_id, true)}
                    disabled={forwarding}
                    className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm hover:bg-[hsl(var(--message-hover))] transition-colors disabled:opacity-50"
                    data-testid={`forward-to-dm-${d.dm_id}`}
                  >
                    <div className="w-6 h-6 rounded-full bg-[hsl(var(--surface-3))] flex items-center justify-center text-xs font-semibold">
                      {d.other_user?.display_name?.[0] || d.other_user?.username?.[0] || '?'}
                    </div>
                    <span className="flex-1 text-left truncate">{d.other_user?.display_name || d.other_user?.username}</span>
                  </button>
                ))}
              </>
            )}

            {filteredChannels.length === 0 && filteredDms.length === 0 && (
              <div className="text-center py-6 text-sm text-muted-foreground">No matches found</div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default ForwardDialog;
